import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDownRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { text: "Complex projects", accent: false },
  { text: "need commanders.", accent: true },
  { text: "That\u2019s what we are", accent: false },
];

const INDUSTRIES = [
  "Healthcare",
  "Tech",
  "Finance",
  "Media",
  "Marketing",
  "Sales Plan",
  "AI Implementation",
];

function ParticleField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const count = Math.min(80, Math.floor(canvas.width / 18));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.4 + 0.4,
      }));
    };

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(148,163,184,0.45)";
        ctx.fill();
      }
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.hypot(dx, dy);
          if (d < 110) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(226,185,92,${0.10 * (1 - d / 110)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };

    resize();
    tick();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 h-full w-full" aria-hidden="true" />;
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pb-20 pt-32 lg:pb-28 lg:pt-44">
      <motion.div style={{ y: gridY }} className="absolute inset-0">
        <div className="bg-grid absolute inset-0" />
      </motion.div>
      <ParticleField />
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-gold/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-[-10%] h-[380px] w-[380px] rounded-full bg-cobalt/10 blur-[130px]"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div>
          <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.28em] text-gold sm:text-xs">
            Rapid-deployment project management & rescue
          </p>

          <h1
            data-testid="hero-headline"
            className="font-display text-[clamp(1.9rem,6.2vw,5.4rem)] font-extrabold leading-[1.04] tracking-tight"
          >
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
                <motion.span
                  className={`block ${l.accent ? "text-gold" : "text-white"}`}
                  initial={{ y: "115%", rotate: 2.5 }}
                  animate={{ y: "0%", rotate: 0 }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.13, ease: EASE }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            Astra Consult provides experienced project leadership to drive
            high-stakes initiatives from kickoff to completion and put stalled
            projects back on track. Short-term or long-term, across seven
            industries, and ready to step in quickly when timing is tight.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-emergency"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.18em] text-obsidian transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_40px_rgba(226,185,92,0.5)]"
            >
              Deploy emergency PM
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-audit"
              onClick={() => scrollToId("services")}
              className="inline-flex items-center gap-3 rounded-full border border-white/15 px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.18em] text-slate-200 transition-all duration-300 hover:border-white/40 hover:bg-white/5"
            >
              Explore services
              <ArrowDownRight className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"
          >
            <span>Short-term & long-term PM</span>
            <span className="text-slate-700">/</span>
            <span>Quick-turnaround sprints</span>
            <span className="text-slate-700">/</span>
            <span>7 industries served</span>
          </motion.div>
        </div>

      </div>
    </section>
  );
}
